import { AttributeKindEnum } from "@brewbase/db/types";
import type { BaseTemplateAttribute, Block } from "./types";

export interface BlockModel {
  blocks: Block[];
  flatIds: number[];
  getInsertPosition: (blockIndex: number) => number;
}

/**
 * Pure block-model builder — groups a flat attribute list into scalar /
 * tabular / instruction blocks. Pure (not a hook) so it can be mapped over a
 * variable number of editor sections inside a single useMemo.
 */
export function buildBlockModel(attributes: BaseTemplateAttribute[]): BlockModel {
  const blocks: Block[] = [];
  let currentScalars: BaseTemplateAttribute[] = [];

  for (const attr of attributes) {
    if (
      attr.kind === AttributeKindEnum.TABULAR_DATA ||
      attr.kind === AttributeKindEnum.INSTRUCTION
    ) {
      if (currentScalars.length > 0) {
        blocks.push({ type: "scalar", attributes: currentScalars });
        currentScalars = [];
      }
      blocks.push(
        attr.kind === AttributeKindEnum.INSTRUCTION
          ? { type: "instruction", attribute: attr }
          : { type: "tabular", attribute: attr },
      );
    } else {
      currentScalars.push(attr);
    }
  }

  if (currentScalars.length > 0) {
    blocks.push({ type: "scalar", attributes: currentScalars });
  }

  const flatIds = attributes.map((a) => a.id);

  // Maps a block index to the flat attribute position for inserting before it.
  const positions: number[] = [];
  let offset = 0;
  for (const block of blocks) {
    positions.push(offset);
    offset += block.type === "scalar" ? block.attributes.length : 1;
  }
  const getInsertPosition = (blockIndex: number) =>
    blockIndex < positions.length ? positions[blockIndex] : attributes.length;

  return { blocks, flatIds, getInsertPosition };
}
